import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { usePlanFeature } from 'src/hooks/usePlanFeature';
import { isUserAdmin } from 'src/utils/roleUtils';
// routes
import { paths } from 'src/routes/paths';
// components
import Box from '@mui/material/Box';
import { Icon } from '@iconify/react';
import Label from 'src/components/label';
import SvgColor from 'src/components/svg-color';
import it from 'date-fns/esm/locale/it/index';
// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
  // OR
  // <Iconify icon="fluent:mail-24-filled" />
  // https://icon-sets.iconify.design/solar/
  // https://www.streamlinehq.com/icons
);

const ToggleIcon = ({ outlined, filled }) => <Box sx={{ display: 'flex' }}>{filled}</Box>;

ToggleIcon.propTypes = {
  outlined: PropTypes.node,
  filled: PropTypes.node,
};

export const ICONS = {
  // 1. Setup Required / Rocket
  setup: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L4 9V21H20V9L12 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 21V12H15V21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="7" r="1" fill="currentColor" />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2L4 9V21H20V9L12 2Z" fill="currentColor" />
          <path d="M9 21V12H15V21" fill="white" />
        </svg>
      }
    />
  ),
  // 2. Talent Acquisition - Users/Team
  tag: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
          <circle cx="17" cy="7" r="3" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M3 21C3 17.686 5.686 15 9 15C12.314 15 15 17.686 15 21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path
            d="M17 15C19.209 15 21 16.791 21 19V21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="9" cy="7" r="3.5" fill="currentColor" />
          <circle cx="17" cy="7" r="3" fill="currentColor" opacity="0.6" />
          <path d="M2 21C2 17.134 5.134 14 9 14C12.866 14 16 17.134 16 21H2Z" fill="currentColor" />
          <path
            d="M17 14C19.761 14 22 16.239 22 19V21H16V19C16 16.239 17 14 17 14Z"
            fill="currentColor"
            opacity="0.6"
          />
        </svg>
      }
    />
  ),
  // 3. Human Resources - Briefcase
  interview: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 7V5C8 3.895 8.895 3 10 3H14C15.105 3 16 3.895 16 5V7"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path d="M3 12H21" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8 7V5C8 3.895 8.895 3 10 3H14C15.105 3 16 3.895 16 5V7"
            fill="currentColor"
            opacity="0.5"
          />
          <path
            d="M8 7V5C8 3.895 8.895 3 10 3H14C15.105 3 16 3.895 16 5V7"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <rect x="3" y="7" width="18" height="13" rx="2" fill="currentColor" />
          <path d="M3 12H21" stroke="white" strokeWidth="1.5" />
        </svg>
      }
    />
  ),
  // 8. Client - Building
  client: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="4" y="4" width="16" height="17" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 8H10M14 8H16M8 12H10M14 12H16M8 16H10M14 16H16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path d="M11 21V17H13V21" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="4" y="4" width="16" height="17" rx="1" fill="currentColor" />
          <path
            d="M8 8H10M14 8H16M8 12H10M14 12H16M8 16H10M14 16H16"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <rect x="10" y="17" width="4" height="4" fill="white" />
        </svg>
      }
    />
  ),
  // vendor: <img src="/assets/icons/sidebar/Vendor.png" alt="Vendor Icon" width="52" height="28" />,
  vendor: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="4" y="4" width="16" height="17" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 8H10M14 8H16M8 12H10M14 12H16M8 16H10M14 16H16"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <path d="M11 21V17H13V21" stroke="currentColor" strokeWidth="1.5" />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="4" y="4" width="16" height="17" rx="1" fill="currentColor" />
          <path
            d="M8 8H10M14 8H16M8 12H10M14 12H16M8 16H10M14 16H16"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
          <rect x="10" y="17" width="4" height="4" fill="white" />
        </svg>
      }
    />
  ),
  // 4. User Management - User
  admin: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M5 21C5 17.134 8.134 14 12 14C15.866 14 19 17.134 19 21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="8" r="4.5" fill="currentColor" />
          <path
            d="M4 21C4 16.582 7.582 13 12 13C16.418 13 20 16.582 20 21H4Z"
            fill="currentColor"
            opacity="0.7"
          />
        </svg>
      }
    />
  ),
  // 7. Project - Folder/Kanban
  project: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 7C3 5.895 3.895 5 5 5H9L11 7H19C20.105 7 21 7.895 21 9V18C21 19.105 20.105 20 19 20H5C3.895 20 3 19.105 3 18V7Z"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M7 11V15M12 11V15M17 11V15"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3 7C3 5.895 3.895 5 5 5H9L11 7H19C20.105 7 21 7.895 21 9V18C21 19.105 20.105 20 19 20H5C3.895 20 3 19.105 3 18V7Z"
            fill="currentColor"
          />
          <path
            d="M7 11V15M12 11V15M17 11V15"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
    />
  ),
  // 9. Master Data - Database
  masters: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="12" cy="6" rx="8" ry="3" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M4 6V18C4 19.657 7.582 21 12 21C16.418 21 20 19.657 20 18V6"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M4 12C4 13.657 7.582 15 12 15C16.418 15 20 13.657 20 12"
            stroke="currentColor"
            strokeWidth="1.5"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="12" cy="6" rx="8" ry="3" fill="currentColor" />
          <path
            d="M4 6V12C4 13.657 7.582 15 12 15C16.418 15 20 13.657 20 12V6"
            fill="currentColor"
            opacity="0.7"
          />
          <path
            d="M4 12V18C4 19.657 7.582 21 12 21C16.418 21 20 19.657 20 18V12"
            fill="currentColor"
            opacity="0.5"
          />
          <ellipse cx="12" cy="12" rx="8" ry="3" fill="currentColor" opacity="0.8" />
        </svg>
      }
    />
  ),
  // 5. Approvals - Check Circle
  approvals: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M8 12L11 15L16 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="12" r="10" fill="currentColor" />
          <path
            d="M8 12L11 15L16 9"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    />
  ),
  inventory: (
    <img src="/assets/icons/sidebar/Inventory.png" alt="Inventory Icon" width="62" height="28" />
  ),
  assets: (
    <img src="/assets/icons/sidebar/assets.png" alt="Inventory Icon" width="62" height="28" />
  ),
  menuitems: (
    <>
      <img
        src="/assets/icons/menu-items/mdi--eye.png"
        alt="Eye Icon"
        style={{
          margin: '10px', // Adds spacing outside the image
          padding: '15px', // Adds spacing inside the image's border
        }}
      />
    </>
  ),
  procurement: (
    <img src="/assets/icons/sidebar/procurement.png" alt="Inventory Icon" width="62" height="28" />
  ),
  // 6. Preferences - Settings/Gear
  preferences: (
    <ToggleIcon
      outlined={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="lucide lucide-monitor-cog-icon lucide-monitor-cog"
        >
          <path d="M12 17v4" />
          <path d="m14.305 7.53.923-.382" />
          <path d="m15.228 4.852-.923-.383" />
          <path d="m16.852 3.228-.383-.924" />
          <path d="m16.852 8.772-.383.923" />
          <path d="m19.148 3.228.383-.924" />
          <path d="m19.53 9.696-.382-.924" />
          <path d="m20.772 4.852.924-.383" />
          <path d="m20.772 7.148.924.383" />
          <path d="M22 13v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" />
          <path d="M8 21h8" />
          <circle cx="18" cy="6" r="3" />
        </svg>
      }
      filled={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path
            d="M22 13v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7"
            fill="currentColor"
            opacity="0.4"
          />
          <circle cx="18" cy="6" r="3" fill="currentColor" opacity="0.5" />
          <path d="M12 17v4" />
          <path d="m14.305 7.53.923-.382" />
          <path d="m15.228 4.852-.923-.383" />
          <path d="m16.852 3.228-.383-.924" />
          <path d="m16.852 8.772-.383.923" />
          <path d="m19.148 3.228.383-.924" />
          <path d="m19.53 9.696-.382-.924" />
          <path d="m20.772 4.852.924-.383" />
          <path d="m20.772 7.148.924.383" />
          <path d="M22 13v2a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7" />
          <path d="M8 21h8" />
          <circle cx="18" cy="6" r="3" />
        </svg>
      }
    />
  ),
  // 11. To Do - Checklist
  todo: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M7 10L9 12L12 9M7 16L9 18L12 15"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M15 11H19M15 17H19"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" fill="currentColor" />
          <path
            d="M7 10L9 12L12 9M7 16L9 18L12 15"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M15 11H19M15 17H19" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      }
    />
  ),
  // 10. My Requisitions - Clipboard List
  myreq: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="6" y="4" width="12" height="17" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <path d="M9 2H15V4H9V2Z" stroke="currentColor" strokeWidth="1.5" />
          <path
            d="M9 9H15M9 13H15M9 17H12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="6" y="4" width="12" height="17" rx="2" fill="currentColor" />
          <rect x="9" y="2" width="6" height="3" rx="1" fill="currentColor" />
          <rect x="9" y="2" width="6" height="3" rx="1" stroke="white" strokeWidth="0.5" />
          <path
            d="M9 9H15M9 13H15M9 17H12"
            stroke="white"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      }
    />
  ),
  // tag: <Icon icon="grommet-icons:group" width="25" height="20" />,
  // project: <Icon icon="eos-icons:project-outlined" width="26" height="26" />,
  person: <Icon icon="f7:doc-person" width="56" height="56" />,
  // approval: <Icon icon="fluent-mdl2:document-approval" width="20" height="20" />,
  // inventory: <Icon icon="material-symbols-light:inventory-rounded" width="50px" height="50px" />,
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: <Icon icon="la:user-tie" width="32" height="32" />,
  employee: <Icon icon="clarity:employee-group-line" width="26" height="36" />,
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  // calendar: icon('ic_calendar'),
  calendar: <Icon icon="fluent-emoji-flat:calendar" width="56" height="56" />,
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
  home: (
    <ToggleIcon
      outlined={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L4 9V21H20V9L12 2Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9 21V12H15V21"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
      filled={
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M12 2L4 9V21H20V9L12 2Z" fill="currentColor" />
          <path d="M9 21V12H15V21" fill="white" />
        </svg>
      }
    />
  ),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { modules = {}, screens = {} } = useSelector((state) => state.permission || {});
  const { enabled: approvalEnabled } = usePlanFeature('REQUISITION_APPROVAL');

  const normalizedModules = useMemo(
    () =>
      Object.entries(modules).reduce((acc, [key, value]) => {
        acc[key.trim().toLowerCase()] = value;
        return acc;
      }, {}),
    [modules]
  );
  const normalizedScreens = useMemo(
    () =>
      Object.entries(screens).reduce((acc, [key, value]) => {
        acc[key.trim().toLowerCase()] = value;
        return acc;
      }, {}),
    [screens]
  );

  const pendingMaterialCount = useSelector((state) => state?.pendingCounts?.pendingMaterialCount);
  const pendingCandCanApprovalCount = useSelector(
    (state) => state?.pendingCounts?.pendingCandCanApprovalCount
  );

  const pendingCandApprovalCount = useSelector(
    (state) => state?.pendingCounts?.pendingCandApprovalCount
  );
  const pendingPurchaseCount = useSelector((state) => state?.pendingCounts?.pendingPurchaseCount);
  const totalPendingApprCount = useSelector(
    (state) => state?.pendingCounts?.totalPendingApprvlCount
  );
  const totalPendingCandApprCount = useSelector(
    (state) => state?.pendingCounts?.totalPendingCandApprvlCount
  );

  const pendingMasterDataCount = useSelector(
    (state) => state?.pendingCounts?.pendingMasterDataCount
  );
  const authSites = useSelector((state) => state?.auth?.sites);
  const authUser = useSelector((state) => state?.auth?.user);

  const isAdmin = isUserAdmin(authSites);
  const isVendor = useMemo(() => {
    const selectedSite = JSON.parse(localStorage.getItem('selectedSubscrSite') || '{}');
    const availableRoles = JSON.parse(localStorage.getItem('availableRoles') || '[]');
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    const user = authUser || localUser;

    return Boolean(
      (user?.vendorId != null && user?.vendorId !== '' && user?.vendorId !== 'null' && user?.vendorId !== undefined) ||
      (selectedSite?.roleName && String(selectedSite.roleName).toLowerCase().includes('vendor')) ||
      (Array.isArray(availableRoles) && availableRoles.some((r) => String(r?.roleName).toLowerCase().includes('vendor'))) ||
      (Array.isArray(authSites) && authSites.some((r) => String(r?.roleName).toLowerCase().includes('vendor'))) ||
      (user?.role && String(user.role).toLowerCase().includes('vendor')) ||
      (Array.isArray(user?.roles) && user.roles.some((r) => String(r).toLowerCase().includes('vendor')))
    );
  }, [authSites, authUser]);

  const data = useMemo(
    () => [
      {
        subheader: '',
        items: [
          {
            title: 'Home',
            path: paths.dashboard.root,
            icon: ICONS.home,
            exact: true,
          },
          {
            title: 'Talent Acquisition (TA)',
            path: paths.dashboard.talentAcquisition.root,
            icon: ICONS.tag,
            permission: { type: 'module', name: 'Talent Aquisition' },
            children: [
              {
                title: 'Job Description (JD)',
                path: paths.dashboard.talentAcquisition.master.PastJd,
                permission: { type: 'screen', name: 'Job Description' },
              },
              {
                title: 'Candidate Requisitions',
                path: paths.dashboard.talentAcquisition.CanReqTagList,
                permission: { type: 'screen', name: 'Candidate Requisition' },
              },
              {
                title: 'Candidate Pool',
                path: paths.dashboard.talentAcquisition.master.ListCandidate,
                permission: { type: 'screen', name: 'Candidates' },
              },
              {
                title: 'Candidate Matching',
                path: paths.dashboard.talentAcquisition.RequisitionMapping,
                permission: { type: 'screen', name: 'Candidate Mapping' },
                feature: 'AI_CANDIDATE_MATCHING',
              },
              {
                title: 'Schedule Interview',
                path: paths.dashboard.talentAcquisition.listAndCalendarView,
                permission: { type: 'screen', name: 'Schedule Interview' },
              },
              {
                title: 'Interview Pipeline',
                path: paths.dashboard.talentAcquisition.InterviewDashboard,
                permission: { type: 'screen', name: 'Interview Pipeline' },
              },
              {
                title: 'My Interviews',
                path: paths.dashboard.talentAcquisition.ListInterview,
                permission: { type: 'screen', name: 'My Interviews' },
              },
              {
                title: 'Selected Candidates',
                path: paths.dashboard.talentAcquisition.SelectedCandidates,
                permission: { type: 'screen', name: 'Selected Candidates' },
              },
              {
                title: 'Offered Candidates',
                path: paths.dashboard.talentAcquisition.OfferedCandidates,
                permission: { type: 'screen', name: 'Offered Candidates' },
              },
              {
                title: 'Under Review Candidates',
                path: paths.dashboard.talentAcquisition.master.ListOfUnderReviewedCand,
                permission: { type: 'screen', name: 'Under Review Candidates' },
              },
              // {
              //   title: 'Offer Letter Generator',
              //   path: paths.dashboard.talentAcquisition.OfferLetterGenerate,
              //   permission: { type: 'screen', name: 'Offer Letter' },
              // },
              //
              //   title: 'Offer Letter Template',
              //   path: paths.dashboard.talentAcquisition.OfferLetterCreation,
              //   permission: { type: 'screen', name: 'Offer Letter' },
              // },
              // {
              //   title: 'Offer Letter Preview',
              //   path: paths.dashboard.talentAcquisition.OfferLetterPreview,
              //   permission: { type: 'screen', name: 'Offer Letter' },
              // },
            ],
          },
          // Tag Dashboard
          // {
          //   title: 'Tag',
          //   path: paths.dashboard.general.tag,
          //   icon: ICONS.user,
          // },
          // User Master for User Registration
          // {
          //   title: 'User',
          //   path: paths.dashboard.master.usermst,
          //   icon: ICONS.person,
          // },
        ],
      },
      {
        subheader: '',
        items: [
          {
            title: 'Human Resources (HR)',
            path: paths.dashboard.humanResources.root,
            icon: ICONS.interview,
            permission: { type: 'module', name: 'Human Resources' },
            children: [
              {
                title: 'Employee',
                path: paths.dashboard.humanResources.ListEmployee,
                permission: { type: 'screen', name: 'Employee' },
              },
              {
                title: 'Official Email Addon',
                path: paths.dashboard.humanResources.AddOffEmail,
                permission: { type: 'screen', name: 'Employee' }, // here should change the values to OffEmailAddon / AddOffEmail
              },
              {
                title: 'Under Review Employees',
                path: paths.dashboard.humanResources.ListOfUnderReviewedEmp,
                permission: { type: 'screen', name: 'Under Reviewed Employees' }, // here should change the values to OffEmailAddon / AddOffEmail
              },
            ],
          },
        ],
      },
      // {
      //   subheader: '',
      //   items: [
      //     {
      //       title: 'Inventory',
      //       path: paths.dashboard.inventory.root,
      //       icon: ICONS.inventory,
      //       permission: { type: 'module', name: 'Inventory' },
      //       children: [
      //         // {
      //         //   title: 'Material Requisition',
      //         //   path: paths.dashboard.inventory.ItemRequisition,
      //         //   permission: { type: 'screen', name: 'Item Requisition' },
      //         // },

      //         {
      //           title: 'Inventory Transactions',
      //           path: paths.dashboard.inventory.InvTransactionList,
      //           permission: { type: 'screen', name: 'Inventory Transactions' },
      //         },
      //         {
      //           title: 'On Hand Quantity',
      //           path: paths.dashboard.inventory.InvOnHandList,
      //           permission: { type: 'screen', name: 'On Hand Quantity' },
      //         },
      //         {
      //           title: 'Physical inventory',
      //           path: paths.dashboard.inventory.AllPhysicalInventoryList,
      //           permission: { type: 'screen', name: 'Physical Inventory' },
      //         },
      //         {
      //           title: 'Material inspection',
      //           path: paths.dashboard.inventory.InspectionList,
      //           permission: { type: 'screen', name: 'Material Inspection' },
      //         },
      //         {
      //           title: 'Item',
      //           path: paths.dashboard.inventory.master.ItemList,
      //           permission: { type: 'screen', name: 'Item' },
      //         },
      //         {
      //           title: 'Approved Item Requisition',
      //           path: paths.dashboard.inventory.AllItemReqTag,
      //           permission: { type: 'screen', name: 'Approved Item Requisition' },
      //         },
      //         {
      //           title: 'Work Order',
      //           path: paths.dashboard.inventory.workOrderList,
      //           permission: { type: 'screen', name: 'Item Requisition' },
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   subheader: '',
      //   items: [
      //     {
      //       title: 'Assets',
      //       path: paths.dashboard.assets.root,
      //       icon: ICONS.assets,
      //       permission: { type: 'module', name: 'Assets' },
      //       children: [
      //         {
      //           title: 'My Assets',
      //           path: paths.dashboard.assets.AssetTrackerList,
      //           permission: { type: 'screen', name: 'My Assets' },
      //         },
      //         {
      //           title: 'Asset Tracker',
      //           path: paths.dashboard.assets.AssetTrackerAdminList,
      //           permission: { type: 'screen', name: 'Asset Tracker' },
      //         },
      //         {
      //           title: 'Reserved Assets',
      //           path: paths.dashboard.assets.ReservedAssets,
      //           permission: { type: 'screen', name: 'Reserved Assets' },
      //         },
      //       ],
      //     },
      //   ],
      // },
      {
        items: [
          {
            title: 'User Management',
            path: paths.dashboard.admin.root,
            icon: ICONS.admin,
            permission: { type: 'module', name: 'User Management' },
            children: [
              {
                title: 'User',
                path: paths.dashboard.admin.usermst,
                permission: { type: 'screen', name: 'User' },
              },
              {
                title: 'User Role Assignment',
                path: paths.dashboard.admin.UserRoleAssign,
                permission: { type: 'screen', name: 'User Role Assignment' },
              },
              {
                title: 'Role Permission',
                path: paths.dashboard.admin.rolePermission,
                permission: { type: 'screen', name: 'Role Permission' },
              },
            ],
          },
        ],
      },
      {
        // subheader: 'Approvals',
        items: [
          {
            title: 'Approvals',
            path: paths.dashboard.ManagerApproval.root,
            icon: ICONS.approvals,
            permission: { type: 'module', name: 'Approvals' },
            feature: 'REQUISITION_APPROVAL',
            info:
              totalPendingApprCount && totalPendingApprCount > 0 ? (
                <Label color="warning">{totalPendingApprCount}</Label>
              ) : null,

            children: [
              {
                title: `Candidate Requisitions(Manager Approval)`,
                path: paths.dashboard.ManagerApproval.root,
                permission: { type: 'screen', name: 'Candidate Requisitions Approval' },
                info:
                  totalPendingCandApprCount && totalPendingCandApprCount > 0 ? (
                    <Label color="warning">{totalPendingCandApprCount}</Label>
                  ) : null,
                children: [
                  {
                    title: 'Candidate Requisitions(Approval)',
                    path: paths.dashboard.ManagerApproval.CandidateRequisitionApproval
                      .CanReqApproval,
                    permission: { type: 'screen', name: 'Candidate Requisitions Approval' },
                    info:
                      pendingCandApprovalCount && pendingCandApprovalCount > 0 ? (
                        <Label color="warning">{pendingCandApprovalCount}</Label>
                      ) : null,
                  },
                  {
                    title: 'Cancellation Requisitions',
                    path: paths.dashboard.ManagerApproval.CandidateRequisitionApproval.CancelCanReq,
                    permission: { type: 'screen', name: 'Candidate Requisitions Approval' },
                    info:
                      pendingCandCanApprovalCount && pendingCandCanApprovalCount > 0 ? (
                        <Label color="warning">{pendingCandCanApprovalCount}</Label>
                      ) : null,
                  },
                ],
              },
              // {
              //   title: `Material Requisitions`,
              //   path: paths.dashboard.ManagerApproval.MaterialReqApproval,
              //   permission: { type: 'screen', name: 'Material Requisitions Approvals' },
              //   info:
              //     pendingMaterialCount && pendingMaterialCount > 0 ? (
              //       <Label color="warning">{pendingMaterialCount}</Label>
              //     ) : null,
              // },
              // {
              //   title: `Purchase Requisitions`,
              //   path: paths.dashboard.ManagerApproval.PurReqApproval,
              //   permission: { type: 'screen', name: 'Purchase Requisitions Approval' },
              //   info:
              //     pendingPurchaseCount && pendingPurchaseCount > 0 ? (
              //       <Label color="warning">{pendingPurchaseCount}</Label>
              //     ) : null,
              // },
              {
                title: `Master Data`,
                path: paths.dashboard.ManagerApproval.MstDataApproval,
                permission: { type: 'screen', name: 'Candidate Requisitions Approval' },
                info:
                  pendingMasterDataCount && pendingMasterDataCount > 0 ? (
                    <Label color="warning">{pendingMasterDataCount}</Label>
                  ) : null,
              },
            ],
          },
        ],
      },
      {
        subheader: '',
        items: [
          {
            title: 'Preferences',
            path: paths.dashboard.preferences.root,
            icon: ICONS.preferences,
            permission: { type: 'module', name: 'Preferences' },
            children: [
              {
                title: 'Preferences',
                path: paths.dashboard.preferences.TenantConfig,
                permission: { type: 'screen', name: 'Preferences' },
              },
            ],
          },
        ],
      },
      // {
      //   subheader: '',
      //   items: [
      //     {
      //       title: 'Procurement',
      //       path: paths.dashboard.procurement.root,
      //       icon: ICONS.procurement,
      //       permission: { type: 'module', name: 'Procurement' },
      //       children: [
      //         {
      //           title: 'Approved Purchase Requisitions',
      //           path: paths.dashboard.procurement.PurchaseRequisition,
      //           permission: { type: 'screen', name: 'Purchase Requisitions' },
      //         },
      //       ],
      //     },
      //   ],
      // },

      // =======================
      // ✅ PROJECT (UNCHANGED)
      // =======================
      {
        subheader: '',
        items: [
          {
            title: 'Project',
            path: paths.dashboard.project.roots,
            icon: ICONS.project,
            permission: { type: 'module', name: 'Project' },
            children: [
              {
                title: 'Project',
                path: paths.dashboard.project.projectlist,
                permission: { type: 'screen', name: 'Project' },
              },
              {
                title: 'Project Allocation',
                path: paths.dashboard.project.projectallocationform,
                permission: { type: 'screen', name: 'Project Allocation' },
              },
            ],
          },
        ],
      },

      // =======================
      // ✅ CLIENT (NEW POSITION)
      // =======================
      {
        subheader: '',
        items: [
          {
            title: 'Client',
            path: paths.dashboard.masters.ListClient,
            icon: ICONS.client,
            permission: { type: 'screen', name: 'Client' }, //  screen, not module
          },
        ],
      },

      {
        subheader: '',
        items: [
          {
            title: 'Vendor',
            path: paths.dashboard.masters.ListVendor,
            icon: ICONS.vendor,
            permission: { type: 'screen', name: 'Vendor' }, //  screen, not module
          },
        ],
      },



      // =======================
      // ✅ MASTER DATA (FLATTENED)
      // =======================
      {
        subheader: '',
        items: [
          {
            title: 'Master Data',
            path: paths.dashboard.masters.root,
            icon: ICONS.masters,
            permission: { type: 'module', name: 'Master Data' },

            children: [
              // ✅ ALL ITEMS DIRECT (NO GLOBAL / LOCAL)

              // {
              //   title: 'Action',
              //   path: paths.dashboard.masters.globalMasters.ActionTables,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Address Purpose',
                path: paths.dashboard.masters.globalMasters.AddressPurpose,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Bank Account Type',
                path: paths.dashboard.masters.globalMasters.BankAccountType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Bank Name',
                path: paths.dashboard.masters.globalMasters.BankName,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Blood Group',
                path: paths.dashboard.masters.globalMasters.BloodGroup,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Business Role',
                path: paths.dashboard.masters.globalMasters.BusinessRole,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Business Unit',
                path: paths.dashboard.masters.globalMasters.BusinessUnit,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Certificate',
                path: paths.dashboard.masters.globalMasters.Certificates,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Contract Type',
                path: paths.dashboard.masters.globalMasters.ContractType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Delivery Unit',
                path: paths.dashboard.masters.globalMasters.DeliveryUnit,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Department',
                path: paths.dashboard.masters.globalMasters.Department,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Designation',
                path: paths.dashboard.masters.globalMasters.Designation,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Domain',
                path: paths.dashboard.masters.globalMasters.Domain,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Employment Type',
                path: paths.dashboard.masters.globalMasters.EmploymentType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Gender',
                path: paths.dashboard.masters.globalMasters.Gender,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Goods',
              //   path: paths.dashboard.masters.globalMasters.Goods,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'HSN/SAC',
                path: paths.dashboard.masters.globalMasters.HsnSac,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Institute',
                path: paths.dashboard.masters.globalMasters.Institute,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Interview Level',
                path: paths.dashboard.masters.globalMasters.InterviewLevel,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Interview Status',
                path: paths.dashboard.masters.globalMasters.InterviewStatus,
                permission: { type: 'screen', name: 'Master' },
              },

              // ✅ LOCAL MASTER ALSO INCLUDED HERE
              // {
              //   title: 'Item Category',
              //   path: paths.dashboard.masters.globalMasters.ItemCategoryMst,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Item Condition',
              //   path: paths.dashboard.masters.globalMasters.ItemCondition,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Item Type',
              //   path: paths.dashboard.masters.globalMasters.ItemType,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Language',
                path: paths.dashboard.masters.globalMasters.Language,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Marital Status',
                path: paths.dashboard.masters.globalMasters.MaritalStatus,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Meeting Platform',
                path: paths.dashboard.masters.globalMasters.MeetingPlatform,
                permission: { type: 'screen', name: 'Master' },
              },

              {
                title: 'Organization Type',
                path: paths.dashboard.masters.globalMasters.OrganizationType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Position Level',
                path: paths.dashboard.masters.globalMasters.PositionLevel,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Practice',
                path: paths.dashboard.masters.globalMasters.Practice,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Preference Type',
                path: paths.dashboard.masters.globalMasters.PreferenceType,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Project Status',
              //   path: paths.dashboard.masters.globalMasters.ProjectStatus,
              //   permission: { type: 'screen', name: 'Master' },
              // },

              {
                title: 'Project Type',
                path: paths.dashboard.masters.globalMasters.PTList,
                permission: { type: 'screen', name: 'Master' },
              },

              {
                title: 'Project Category',
                path: paths.dashboard.masters.globalMasters.ProjectCategory,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Qualification',
                path: paths.dashboard.masters.globalMasters.Qualification,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Qualification Stream',
                path: paths.dashboard.masters.globalMasters.QualificationStreams,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Qualification Type',
                path: paths.dashboard.masters.globalMasters.QualificationType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'New Employee Question',
                path: paths.dashboard.masters.globalMasters.Questions,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Item Return Reason',
              //   path: paths.dashboard.masters.globalMasters.Reason,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Relationship',
                path: paths.dashboard.masters.globalMasters.Relationship,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Religion',
                path: paths.dashboard.masters.globalMasters.Religion,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Requisition Status',
              //   path: paths.dashboard.masters.globalMasters.ReqStatus,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Revenue Type',
                path: paths.dashboard.masters.globalMasters.RevenueType,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Role',
              //   path: paths.dashboard.masters.globalMasters.Role,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Salutation',
                path: paths.dashboard.masters.globalMasters.NameTitle,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Service Category',
                path: paths.dashboard.masters.globalMasters.ServiceCategory,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Skill',
                path: paths.dashboard.masters.globalMasters.SkillList,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Skill Category',
                path: paths.dashboard.masters.globalMasters.SkillCategory,
                permission: { type: 'screen', name: 'Master' },
              },

              // {
              //   title: 'Sub Inventory Type',
              //   path: paths.dashboard.masters.globalMasters.SubInvType,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Source Of Hire',
                path: paths.dashboard.masters.globalMasters.SourceOfHire,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'TDS Section',
              //   path: paths.dashboard.masters.globalMasters.TDSSection,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Inventory Transaction Type',
              //   path: paths.dashboard.masters.globalMasters.TransactionType,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'University',
                path: paths.dashboard.masters.globalMasters.University,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Visa Name',
                path: paths.dashboard.masters.globalMasters.VisaName,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Visa Type',
                path: paths.dashboard.masters.globalMasters.VisaType,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Work Mode',
                path: paths.dashboard.masters.globalMasters.WorkMode,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Offer Decline Reason',
                path: paths.dashboard.masters.localMasters.DeclineReason,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Business Role',
                path: paths.dashboard.masters.localMasters.BusinessRole,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Offer Withdraw Reason',
                path: paths.dashboard.masters.localMasters.OfferWithdrawReason,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Employee Onboarding Documents',
                path: paths.dashboard.masters.localMasters.EmpDoc,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Employee Grade',
                path: paths.dashboard.masters.localMasters.EmpGrade,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Inspection Parameter',
              //   path: paths.dashboard.masters.localMasters.InspectionParameter,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Interview Cancel Reason',
                path: paths.dashboard.masters.localMasters.InterviewCancelReason,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Modules',
              //   path: paths.dashboard.masters.localMasters.Modules,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Negative Feedback',
                path: paths.dashboard.masters.localMasters.NegativeFeedback,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Recommendation',
              //   path: paths.dashboard.masters.localMasters.Recommendation,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Report',
              //   path: paths.dashboard.masters.localMasters.Reports,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Role Level',
              //   path: paths.dashboard.masters.localMasters.RoleLevel,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              // {
              //   title: 'Screen',
              //   path: paths.dashboard.masters.localMasters.Screens,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Service',
                path: paths.dashboard.masters.localMasters.Service,
                permission: { type: 'screen', name: 'Master' },
              },

              {
                title: 'Service Plan',
                path: paths.dashboard.masters.localMasters.ServicePlan,
                permission: { type: 'screen', name: 'Master' },
              },
              // {
              //   title: 'Sub Inventory',
              //   path: paths.dashboard.masters.localMasters.SubInventory,
              //   permission: { type: 'screen', name: 'Master' },
              // },
              {
                title: 'Subscriber Site',
                path: paths.dashboard.masters.localMasters.SubscriberSite,
                permission: { type: 'screen', name: 'Master' },
              },
              {
                title: 'Offer Letter Context',
                path: paths.dashboard.masters.localMasters.OfferLetterContext,
                permission: { type: 'screen', name: 'Master' },
              },
            ],
          },
        ],
      },

      // My Requisitions by Role

      {
        // subheader: 'My Requisitions',
        items: [
          {
            title: 'My Requisitions',
            path: paths.dashboard.MyRequisitions.root,
            icon: ICONS.myreq,
            permission: { type: 'module', name: 'My Requisitions' },

            children: [
              {
                title: `Candidate Requisitions(Manager List)`,
                path: paths.dashboard.talentAcquisition.CanReqManagerList,
                permission: { type: 'screen', name: 'Candidate Requisitions(Manager List)' },
              },
              // {
              //   title: `Material Requisitions`,
              //   path: paths.dashboard.inventory.ItemRequisition,
              //   permission: { type: 'screen', name: 'Material Requisitions' },
              // },
              // {
              //   title: 'Purchase Requisitions',
              //   path: paths.dashboard.MyRequisitions.AllItemReqTag,
              //   permission: { type: 'screen', name: 'Approved Item Requisition' },
              // },

              // {
              //   title: 'Purchase Requisitions',
              //   path: paths.dashboard.MyRequisitions.PR,
              //   permission: { type: 'screen', name: 'My Requisitions' },
              // },
            ],
          },
        ],
      },

      ...(isAdmin
        ? [
          {
            subheader: '',
            items: [
              {
                title: 'Billing & Subscription',
                path: paths.dashboard.paymentBilling,
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="5" y="4" width="14" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 6v12M9.5 9h4c1 0 1.5 0.5 1.5 1.25s-0.5 1.25-1.5 1.25h-3c-1 0-1.5 0.5-1.5 1.25s0.5 1.25 1.5 1.25h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
                sx: {
                  borderRadius: '8px',
                  bgcolor: 'background.neutral',
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                  '& .MuiTypography-root': {
                    color: 'text.secondary',
                    fontWeight: 'fontWeightMedium',
                  },
                  '&.active': {
                    bgcolor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981',
                    '&:hover': {
                      bgcolor: 'rgba(16, 185, 129, 0.18)',
                    },
                    '& .MuiTypography-root': {
                      color: '#10B981',
                      fontWeight: '700 !important',
                    },
                    '& svg': {
                      color: '#10B981',
                    }
                  }
                }
              },
            ],
          },
        ]
        : []),

      // {
      //   items: [
      //     {
      //       title: 'To Do',
      //       path: paths.dashboard.ToDo.root,
      //       icon: ICONS.todo,
      //       permission: { type: 'module', name: 'To Do' },
      //       children: [
      //         {
      //           title: 'To Do',
      //           path: paths.dashboard.ToDo.TagToDo,
      //           permission: { type: 'module', name: 'To Do' },
      //         },
      //       ],
      //     },
      //   ],
      // },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      pendingCandApprovalCount,
      pendingCandCanApprovalCount,
      pendingPurchaseCount,
      totalPendingApprCount,
      pendingMaterialCount,
      pendingMasterDataCount,
      approvalEnabled,
      isAdmin,
    ]
  );

  return useMemo(() => {
    if (isVendor) {
      return [
        {
          subheader: '',
          items: [
            {
              title: 'Vendor Portal',
              path: paths.dashboard.vendorPortal.requisitions,
              icon: ICONS.client,
              children: [
                {
                  title: 'Assigned Requisitions',
                  path: paths.dashboard.vendorPortal.requisitions,
                },
              ],
            },
          ],
        },
      ];
    }

    const hasAccess = (permission) => {
      if (!permission) return true;
      const normalizedName = permission.name?.trim()?.toLowerCase();

      if (permission.type === 'module') {
        return modules?.some((m) => m?.trim()?.toLowerCase() === normalizedName);
      }

      if (permission.type === 'screen') {
        return Object.keys(screens || {}).some(
          (key) => key?.trim()?.toLowerCase() === normalizedName
        );
      }

      return true;
    };
    const filterNavItems = (items = []) =>
      items.reduce((acc, item) => {
        if (!hasAccess(item.permission)) {
          return acc;
        }

        // Plan-disabled items are kept but marked for greying out
        const filteredChildren = item.children ? filterNavItems(item.children) : undefined;
        const nextItem = filteredChildren ? { ...item, children: filteredChildren } : item;

        if (nextItem.children && nextItem.children.length === 0 && !nextItem.path) {
          return acc;
        }

        acc.push(nextItem);
        return acc;
      }, []);

    const result = data
      .map((group) => ({
        ...group,
        items: filterNavItems(group.items),
      }))
      .filter((group) => group.items.length > 0);

    return result;
  }, [data, modules, screens, isVendor]);
}
